import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ModalWindow from '@common/components/modal/modal-window';
import ModalContent from '@common/components/modal/modal-content';
import Spinner from '@common/components/spinner';
import { Toggle } from '@common/components/form/inputs/toggle';
import { getIntegrationSettings, updateIntegrationSettings } from '../../actions/integration-settings';
import { AlertService } from '@common/services/alert';

type Props = {
    isOpen: boolean;
    onClose: () => void;
    organisationId: string;
};

export const IntegrationSettingsModal = ({
    isOpen,
    onClose,
    organisationId,
}: Props) => {
    const { t } = useTranslation();
    const [isLoading, setIsLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    const [shouldSync, setShouldSync] = useState(false);
    const [newUserInvites, setNewUserInvites] = useState(true);

    useEffect(() => {
        if (!isOpen) return;

        const fetchSettings = async () => {
            setIsLoading(true);
            try {
                const { data: settings } = await getIntegrationSettings(organisationId);
                setShouldSync(settings.should_sync);
                setNewUserInvites(!settings.skip_user_invite);
            } catch (err) {
                AlertService.error(t('organisation:integration_settings_fetch_error'));
            } finally {
                setIsLoading(false);
            }
        };

        fetchSettings();
    }, [isOpen, organisationId]);

    const onSave = async () => {
        setSaving(true);
        try {
            await updateIntegrationSettings(organisationId, {
                should_sync: shouldSync,
                skip_user_invite: !newUserInvites,
            });
            AlertService.success(t('organisation:integration_settings_save_success'));
            onClose();
        } catch (err) {
            AlertService.error(t('organisation:integration_settings_save_error'));
        } finally {
            setSaving(false);
        }
    };

    return (
      <ModalWindow show={isOpen} onHide={onClose} size="small">
        <ModalContent
          title={t('organisation:integration_settings_title')}
          onHide={onClose}
          confirmButtonText={t('organisation:integration_settings_save_button')}
          onConfirm={onSave}
          confirmButtonLoading={saving}
          confirmButtonDisabled={saving}
        >
          {isLoading ? (
            <div className="tw-flex tw-justify-center tw-items-center tw-h-64">
              <Spinner centered />
            </div>
                ) : (
                  <div className="tw-flex tw-flex-col tw-gap-5 tw-py-2">
                    <div className="tw-flex tw-items-start tw-gap-2">
                      <Toggle
                        value={shouldSync}
                        onChange={(val) => setShouldSync(val)}
                      />
                      <div className="tw-flex-1">
                        <div className="tw-font-medium">{t('organisation:automatic_sync')}</div>
                        <div className="tw-text-sm tw-text-gray-500">
                          {t('organisation:automatic_sync_description')}
                        </div>
                      </div>
                    </div>
                    <div className="tw-flex tw-items-start tw-gap-2">
                      <Toggle
                        value={newUserInvites}
                        onChange={(val) => setNewUserInvites(val)}
                      />
                      <div className="tw-flex-1">
                        <div className="tw-font-medium">{t('organisation:new_user_invitations')}</div>
                        <div className="tw-text-sm tw-text-gray-500">
                          {t('organisation:new_user_invitations_description')}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
        </ModalContent>
      </ModalWindow>
    );
};
